export const FIRST_PARTY_TRANSPORT_FAILURE_EVENT = 'pointcraft:first-party-transport-failure';

type AxiosTransportErrorLike = {
  code?: unknown;
  isAxiosError?: unknown;
  name?: unknown;
  response?: unknown;
};

function getUrlBase(): string {
  return typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
}

export function isFirstPartyApiRequest(
  url: string,
  requestBaseURL: string,
  configuredApiBaseURL: string
): boolean {
  try {
    const fallbackBase = getUrlBase();
    const configuredApiUrl = new URL(configuredApiBaseURL, fallbackBase);
    const requestBaseUrl = new URL(requestBaseURL, fallbackBase);
    const requestUrl = new URL(url, requestBaseUrl);
    return requestUrl.origin === configuredApiUrl.origin;
  } catch {
    return !/^https?:\/\//i.test(url) && requestBaseURL === configuredApiBaseURL;
  }
}

/**
 * Publishes only transport-level Axios failures. HTTP responses prove that the
 * first-party service was reachable and cancellations are caller intent, so
 * neither should initiate a global connectivity confirmation.
 */
export function reportFirstPartyTransportFailure(error: unknown): boolean {
  if (typeof window === 'undefined' || !error || typeof error !== 'object') return false;

  const transportError = error as AxiosTransportErrorLike;
  const canceled =
    transportError.code === 'ERR_CANCELED' || transportError.name === 'CanceledError';
  if (transportError.isAxiosError !== true || transportError.response || canceled) return false;

  window.dispatchEvent(new Event(FIRST_PARTY_TRANSPORT_FAILURE_EVENT));
  return true;
}

const HTML_DOCUMENT_BODY_PATTERN = /^\s*(<!doctype|<html)/i;

type AxiosSuccessResponseLike = { headers?: unknown; data?: unknown };

/**
 * Publishes first-party 2xx responses whose body is an HTML document. A JSON
 * API answering HTML means an intermediary (dev tunnel placeholder, captive
 * portal, misrouted gateway) responded instead of PointCraft. Detection only:
 * callers keep their return values, and the coordinator's own confirmation
 * probe stays the sole authority — a legitimate HTML payload merely triggers
 * a confirmation that immediately passes.
 */
export function reportFirstPartyHtmlBodyResponse(response: unknown): boolean {
  if (typeof window === 'undefined' || !response || typeof response !== 'object') return false;

  const { headers, data } = response as AxiosSuccessResponseLike;
  const contentType =
    headers && typeof headers === 'object'
      ? (headers as Record<string, unknown>)['content-type']
      : undefined;
  const htmlContentType =
    typeof contentType === 'string' && contentType.toLowerCase().includes('text/html');
  const htmlBody = typeof data === 'string' && HTML_DOCUMENT_BODY_PATTERN.test(data);
  if (!htmlContentType && !htmlBody) return false;

  window.dispatchEvent(new Event(FIRST_PARTY_TRANSPORT_FAILURE_EVENT));
  return true;
}

const GATEWAY_UNAVAILABILITY_STATUSES = new Set([502, 503, 504]);

/**
 * Publishes gateway-class HTTP failures (502/503/504) from first-party calls.
 * These come from infrastructure in front of PointCraft rather than from a
 * healthy PointCraft service, so they request the coordinator's own /status
 * confirmation through the same event. They are never proof of an outage: if
 * /status still answers 200/Online the application stays online. Other HTTP
 * responses (including 500, which ordinary application bugs produce) never
 * initiate a confirmation.
 */
export function reportFirstPartyGatewayFailure(error: unknown): boolean {
  if (typeof window === 'undefined' || !error || typeof error !== 'object') return false;

  const transportError = error as AxiosTransportErrorLike;
  if (transportError.isAxiosError !== true) return false;
  const status = (transportError.response as { status?: unknown } | undefined)?.status;
  if (typeof status !== 'number' || !GATEWAY_UNAVAILABILITY_STATUSES.has(status)) return false;

  window.dispatchEvent(new Event(FIRST_PARTY_TRANSPORT_FAILURE_EVENT));
  return true;
}
